/** Small inline SVG icons in the Explorer/Office idiom (no icon-font dependency). */
type P = { size?: number };
const box = (size = 16) => ({ width: size, height: size, viewBox: '0 0 16 16', 'aria-hidden': true });

export const FolderIcon = ({ size, open }: P & { open?: boolean }) => (
  <svg {...box(size)}>
    <path
      d="M1.5 3.5h4.2l1.4 1.5h7.4v8.5H1.5z"
      fill={open ? '#ffd975' : '#f8c74a'}
      stroke="#c99a1e"
      strokeWidth="0.8"
    />
    {open && <path d="M1.5 13.5l2-6h12l-2 6z" fill="#ffe08f" stroke="#c99a1e" strokeWidth="0.8" />}
  </svg>
);
export const SheetIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <rect x="2" y="1.5" width="12" height="13" rx="1" fill="#fff" stroke="#217346" />
    <path d="M2 5.5h12M2 9.5h12M6.5 1.5v13" stroke="#217346" strokeWidth="0.9" />
    <rect x="2" y="1.5" width="12" height="4" fill="#217346" />
  </svg>
);
export const DocIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <path d="M3 1.5h7l3 3v10H3z" fill="#fff" stroke="#2b579a" />
    <path d="M5 7h6M5 9h6M5 11h4" stroke="#2b579a" strokeWidth="0.9" />
  </svg>
);
export const PlanIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <rect x="1.5" y="2" width="13" height="12" rx="1" fill="#fff" stroke="#7a4fb3" />
    <rect x="3" y="4" width="6" height="2" fill="#7a4fb3" />
    <rect x="6" y="7" width="6" height="2" fill="#a07fd1" />
    <rect x="8" y="10" width="5" height="2" fill="#7a4fb3" />
  </svg>
);
export const LibraryIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" fill="#e6f2eb" stroke="#217346" />
    <path d="M4 5.5h8M4 8h8M4 10.5h5" stroke="#217346" />
  </svg>
);
export const PersonIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <circle cx="8" cy="5.5" r="3" fill="#5b8def" />
    <path d="M2.5 14.5c.6-3 2.8-4.5 5.5-4.5s4.9 1.5 5.5 4.5z" fill="#5b8def" />
  </svg>
);
export const ChatIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <path
      d="M2 3.5a1.5 1.5 0 0 1 1.5-1.5h9A1.5 1.5 0 0 1 14 3.5v6a1.5 1.5 0 0 1-1.5 1.5H7l-3 3v-3h-.5A1.5 1.5 0 0 1 2 9.5z"
      fill="#e8f0fb"
      stroke="#2b579a"
    />
    <path d="M5 5.5h6M5 8h4" stroke="#2b579a" strokeWidth="0.9" />
  </svg>
);
/** M18 "in the cloud": a file that's only in its source (open / download fetches it). */
export const CloudIcon = ({ size = 14 }: P) => (
  <svg {...box(size)}>
    <path
      d="M4.5 12.5h7.2a2.8 2.8 0 0 0 .4-5.6 3.8 3.8 0 0 0-7.3-.9A3.2 3.2 0 0 0 4.5 12.5z"
      fill="#eaf3fb"
      stroke="#2f6fb0"
      strokeWidth="1"
    />
  </svg>
);
/** M18 a connected folder: a folder with a small cloud. */
export const ConnectedFolderIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <path d="M1.5 3.5h4.2l1.4 1.5h7.4v8.5H1.5z" fill="#f8c74a" stroke="#c99a1e" strokeWidth="0.8" />
    <path
      d="M7.2 13.6h5.1a2 2 0 0 0 .3-4 2.7 2.7 0 0 0-5.2-.6 2.3 2.3 0 0 0-.2 4.6z"
      fill="#eaf3fb"
      stroke="#2f6fb0"
      strokeWidth="0.9"
    />
  </svg>
);
export const LockIcon = ({ size = 12 }: P) => (
  <svg {...box(size)}>
    <rect x="3" y="7" width="10" height="7.5" rx="1" fill="#c48a00" />
    <path d="M5 7V5a3 3 0 0 1 6 0v2" fill="none" stroke="#c48a00" strokeWidth="1.6" />
  </svg>
);
export const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    aria-hidden
    style={{ transform: open ? 'rotate(90deg)' : undefined, transition: 'transform .1s' }}
  >
    <path d="M3 1.5L7 5 3 8.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);
export const BoardIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <rect x="1.5" y="9" width="13" height="5" rx="1" fill="#8a5a2b" />
    <rect x="3" y="3" width="10" height="5" rx="1" fill="#fff" stroke="#8a5a2b" />
    <path d="M5 5.5h6" stroke="#8a5a2b" />
  </svg>
);
export const WorkflowIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <circle cx="3.5" cy="8" r="2" fill="#2b579a" />
    <circle cx="12.5" cy="4" r="2" fill="#217346" />
    <circle cx="12.5" cy="12" r="2" fill="#c00000" />
    <path d="M5.5 8L10.5 4.5M5.5 8l5 3.5" stroke="#666" fill="none" />
  </svg>
);
export const GovernanceIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <path d="M8 1.5l6 3v1H2v-1z" fill="#8a5a2b" />
    <path d="M3.5 6.5v6M6.5 6.5v6M9.5 6.5v6M12.5 6.5v6" stroke="#8a5a2b" strokeWidth="1.4" />
    <rect x="2" y="13" width="12" height="1.5" fill="#8a5a2b" />
  </svg>
);
export const ChartIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <path d="M1.5 14.5h13" stroke="#6b6b6b" strokeWidth="0.9" />
    <rect x="2.5" y="8" width="2.6" height="6" rx="0.6" fill="#2e8540" />
    <rect x="6.7" y="4.5" width="2.6" height="9.5" rx="0.6" fill="#e0a100" />
    <rect x="10.9" y="2" width="2.6" height="12" rx="0.6" fill="#c0392b" />
  </svg>
);
export const ReportIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <path d="M3 1.5h7l3 3v10H3z" fill="#fff" stroke="#217346" />
    <circle cx="6" cy="6" r="1.3" fill="#c0392b" />
    <circle cx="6" cy="9" r="1.3" fill="#e0a100" />
    <circle cx="6" cy="12" r="1.3" fill="#2e8540" />
    <path d="M8.2 6h3M8.2 9h3M8.2 12h3" stroke="#217346" strokeWidth="0.9" />
  </svg>
);
/** §20 a diagram: two boxes and a connector. */
export const DiagramIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <rect x="1.5" y="2" width="6" height="4.5" rx="0.8" fill="#e8f0fb" stroke="#2b579a" />
    <rect x="8.5" y="9.5" width="6" height="4.5" rx="0.8" fill="#e6f2eb" stroke="#217346" />
    <path d="M4.5 6.5v5h4" fill="none" stroke="#4a4945" strokeWidth="0.9" />
    <path d="M7.4 10.2l1.3 1.3-1.3 1.3" fill="none" stroke="#4a4945" strokeWidth="0.9" />
  </svg>
);
/** §15 a document by format: Word-blue, Excel-green, PDF-red, image; Fyr-native documents use the Word look. */
export const FormatIcon = ({ format, size }: P & { format: string }) => {
  if (format === 'diagram') return <DiagramIcon size={size} />;
  const colour = { xlsx: '#217346', pptx: '#b7472a', pdf: '#c0392b', image: '#8a5cc2' }[format] ?? '#2b579a';
  const letter = { xlsx: 'X', pptx: 'P', pdf: 'PDF', image: '' }[format] ?? 'W';
  return (
    <svg {...box(size)}>
      <path d="M3 1.5h7l3 3v10H3z" fill="#fff" stroke={colour} />
      {format === 'image' ? (
        <>
          <circle cx="6.2" cy="6.5" r="1.2" fill={colour} />
          <path d="M4 13l3-3.5 2 2 1.5-1.5L12 13z" fill={colour} />
        </>
      ) : (
        <text
          x="8"
          y="11.5"
          textAnchor="middle"
          fontSize={letter.length > 1 ? 4.2 : 6.5}
          fontWeight="700"
          fill={colour}
          fontFamily="system-ui, sans-serif"
        >
          {letter}
        </text>
      )}
    </svg>
  );
};
export const KanbanIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <rect x="1.5" y="2" width="13" height="12" rx="1.5" fill="#fff" stroke="#6b6b6b" />
    <rect x="3" y="4" width="2.6" height="3" rx="0.5" fill="#2a78d6" />
    <rect x="3" y="8" width="2.6" height="2.4" rx="0.5" fill="#2a78d6" />
    <rect x="6.7" y="4" width="2.6" height="5" rx="0.5" fill="#e0a100" />
    <rect x="10.4" y="4" width="2.6" height="2.6" rx="0.5" fill="#2e8540" />
  </svg>
);
export const LearningIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <path d="M8 2.5 1 6l7 3.5L15 6z" fill="#e8f0fb" stroke="#2b579a" strokeLinejoin="round" />
    <path d="M4 7.6V11c0 1 1.8 2 4 2s4-1 4-2V7.6" fill="none" stroke="#2b579a" />
    <path d="M14 6.4v4" stroke="#2b579a" />
  </svg>
);
/** §34 Mail: an envelope. */
export const MailIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <rect x="1.5" y="3.5" width="13" height="9" rx="1.2" fill="#e8f0fb" stroke="#2b579a" />
    <path d="m2 4.2 6 4.6 6-4.6" fill="none" stroke="#2b579a" strokeLinejoin="round" />
  </svg>
);
/** §36 the Fyr Browser: a window with a tab. */
export const BrowserIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <rect x="1.5" y="2.5" width="13" height="11" rx="1.2" fill="#e8f0fb" stroke="#2b579a" />
    <path d="M1.5 5.5h13M4 2.5v3" fill="none" stroke="#2b579a" />
  </svg>
);
/** The personal Dashboard: four tiles. */
export const DashboardIcon = ({ size }: P) => (
  <svg {...box(size)}>
    <rect x="1.5" y="1.5" width="6" height="7" rx="1" fill="#217346" />
    <rect x="8.5" y="1.5" width="6" height="4" rx="1" fill="#e0a100" />
    <rect x="1.5" y="9.5" width="6" height="5" rx="1" fill="#2b579a" />
    <rect x="8.5" y="6.5" width="6" height="8" rx="1" fill="#fff" stroke="#217346" />
  </svg>
);
